import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpErrorResponse } from '@angular/common/http';

import * as _ from 'lodash';
import { of, TimeoutError, throwError } from 'rxjs';
import { catchError, timeout, switchMap } from 'rxjs/operators';

import { MessagesService } from './messages.service';
import { environment, wechatEnvironment } from '../../environments/environment';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';

function getEmbeddedQueryParam(url: URL, key: string): string {
  const searchValue = url.searchParams.get(key);
  if (searchValue !== null) {
    return searchValue;
  }

  const hashQueryIndex = url.hash.indexOf('?');
  if (hashQueryIndex < 0) {
    return '';
  }

  return new URLSearchParams(url.hash.slice(hashQueryIndex + 1)).get(key) || '';
}

function isEmbeddedMode(): boolean {
  try {
    const currentUrl = new URL(window.location.href);
    const embedParam = getEmbeddedQueryParam(currentUrl, 'embed').toLowerCase();
    return Boolean(
      (window as any).__REVENUE_MICRO_APP_EMBED__ ||
      (window as any).__MICRO_APP_ENVIRONMENT__ ||
      (window as any).microApp ||
      embedParam === '1' ||
      embedParam === 'true' ||
      window.self !== window.top,
    );
  } catch (error) {
    return false;
  }
}

@Injectable()
// @ts-ignore
export class BasicService {
  httpBaseHeaders: HttpHeaders;
  httpHeaders: HttpHeaders;

  defaultTimeout = 60000;
  apiUrl = environment.apis.default.url;
  openWorkUrl = environment.apis.default.openWorkUrl;
  authUrl = environment.oAuthConfig.issuer;
  approvalUrl = environment.apis.default.approvalUrl;
  uploadUrl = environment.apis.default.uploadUrl;
  messageUrl = environment.apis.default.messageServiceUrl;
  wechatApiUrl = wechatEnvironment.apis.default.url;
  kpiApiUrl = environment.apis.default.kpiUrl;
  pdaApiUrl = environment.apis.default.pdaUrl;
  constructor(
    protected httpClient: HttpClient,
    protected messagesService: MessagesService,
    private message: NzMessageService,
  ) {
    this.httpBaseHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json; charset=utf-8');
  }


  getHeaders(headers) {
    this.httpHeaders = this.httpBaseHeaders;
    const token = localStorage.getItem('access_token') || '';
    if (token && token !== 'undefined' && token !== 'null') {
      this.httpHeaders = this.httpHeaders
        .set('Authorization', token && `Bearer ${token}` || '');
    }
    // tslint:disable-next-line:forin
    for (const i in headers) {
      this.httpHeaders = this.httpHeaders
        .set(i, headers[i]);
    }

    return this.httpHeaders;
  }

  // getFileHeaders(headers) {
  //   this.httpHeaders = headers || this.httpBaseHeaders;
  //   const token = localStorage.getItem('access_token') || '';
  //   if (token && token !== 'undefined' && token !== 'null') {
  //     this.httpHeaders = this.httpHeaders
  //       .set('Authorization', token && `Bearer ${token}` || '');
  //   }
  //
  //   return this.httpHeaders;
  // }

  getParams(params) {
    return (params instanceof HttpParams) && params || new HttpParams({ fromObject: _.mapValues(params, _.toString) });
  }


  getOptions(headers, params, options: any) {
    const res = {
      headers: this.getHeaders(headers || {}),
      ...(params && { params: this.getParams(params) } || {}),
      ...(options && { options } || {})
    };
    return res;
  }

  // getFileOptions(headers, params, options: any) {
  //   const res = {
  //     headers: this.getFileHeaders(headers),
  //     ...(params && { params: this.getParams(params) } || {}),
  //     ...(options && { options } || {})
  //   };
  //   return res;
  // }


  get({ url, headers, params, options, withError = true, timeout: customTimeout }: {
    url: string, headers?, params?, options?: any, withError?: boolean, timeout?: number
  }) {
    const method = this.httpClient.get<any>(`${url || ''}`, this.getOptions(headers, params, options)).pipe(
      timeout(customTimeout || this.defaultTimeout),
    );

    return withError ? method.pipe(catchError(this.transformError)) : method;
  }

  getFile({ url, headers, params, options, withError = true, timeout: customTimeout }: {
    url: string, headers?, params?, options?: any, withError?: boolean, timeout?: number
  }) {
    const method = this.httpClient.get<any>(`${url || ''}`, {headers, ...(params && { params: this.getParams(params) } || {}), ...options}).pipe(
      timeout(customTimeout || this.defaultTimeout),
    );

    return withError ? method.pipe(catchError(this.transformError)) : method;
  }

  post({ url, body, headers, params, options, withError = true, timeout: customTimeout }: {
    url: string, body?: any, headers?, params?, options?: any, withError?: boolean, timeout?: number
  }) {
    const method =  this.httpClient.post<any>(`${url || ''}`, body || '', this.getOptions(headers, params, options)).pipe(
      timeout(customTimeout || this.defaultTimeout),
    );

    return withError ? method.pipe(catchError(this.transformError)) : method;
  }

  postFile({ url, body, headers, params, options, withError = true, timeout: customTimeout }: {
    url: string, body?: any, headers?, params?, options?: any, withError?: boolean, timeout?: number
  }) {
    const method =  this.httpClient.post<any>(`${url || ''}`, body || '', {headers, ...(params && { params: this.getParams(params) } || {}), ...options}).pipe(
      timeout(customTimeout || this.defaultTimeout),
    );

    return withError ? method.pipe(catchError(this.transformError)) : method;
  }

  delete({ url, headers, params, options, withError = true, timeout: customTimeout }: {
    url: string, headers?, params?, options?: any, withError?: boolean, timeout?: number
  }) {
    const method =  this.httpClient.delete<any>(`${url || ''}`, this.getOptions(headers, params, options)).pipe(
      timeout(customTimeout || this.defaultTimeout),
    );

    return withError ? method.pipe(catchError(this.transformError)) : method;
  }

  put({ url, body, headers, params, options, withError = true, timeout: customTimeout }: {
    url: string, body?: any, headers?, params?, options?: any, withError?: boolean, timeout?: number
  }) {
    const method =  this.httpClient.put<any>(`${url || ''}`, body || '', this.getOptions(headers, params, options)).pipe(
      timeout(customTimeout || this.defaultTimeout),
    );

    return withError ? method.pipe(catchError(this.transformError)) : method;
  }

  transformError(error: HttpErrorResponse | TimeoutError) {
    if (error instanceof TimeoutError) {
      return throwError({ error: {error: { code: 500, message: '请求超时，请检查网络' }}});
    }

    if (!error.status) {
      return throwError({ error: {error: { code: 500, message: '无法连接到服务器，请检查网络' }}});
    }

    if (error.status === 401 || error.error?.error?.code === '401') {
      if (isEmbeddedMode()) {
        return throwError(error);
      }

      localStorage.removeItem('access_token');
      window.location.reload();
    }

    return throwError(error);
  }

}
